import { lazy } from 'react';
import { Badge } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import type { ResourceViewDefinition } from '../types';
import { NoteHeader } from './note-header';
import { statusTone } from './status-tone';

/**
 * Note view registration (GDN-2726) — replaces the standalone note detail page
 * and the bespoke notes list.
 *
 * Note documents store creation time as `time_uploaded` (a Unix timestamp)
 * rather than an ISO `created_at`, which is why the Created column formats a
 * number rather than a string.
 */
export const noteView: ResourceViewDefinition = {
  label: 'Note',
  header: NoteHeader,
  preview: lazy(() =>
    import('../../resource-renderers/note-resource-preview').then(m => ({
      default: m.NoteResourcePreview,
    })),
  ),
  sections: lazy(() =>
    import('./note-sections').then(m => ({ default: m.NoteSections })),
  ),
  columns: [
    { key: 'internal_title', label: 'Title' },
    {
      key: 'status',
      label: 'Status',
      render: value =>
        typeof value === 'string' ? (
          <Badge color={statusTone(value)}>{value.replace('_', ' ')}</Badge>
        ) : (
          '—'
        ),
    },
    // Patient id, not name — admin lists identify patients by id (GDN-2686).
    { key: 'patient_id', label: 'Patient' },
    {
      key: 'is_signed',
      label: 'Signed',
      render: value => (value === true ? 'Yes' : 'No'),
    },
    {
      key: 'time_uploaded',
      label: 'Created',
      render: value =>
        typeof value === 'number'
          ? formatDateTime(new Date(value * 1000).toISOString())
          : '—',
    },
  ],
  filters: [{ key: 'patient_id', label: 'Patient id', type: 'text' }],
};
